import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { DocumentFile } from '../../types';
import type { SelectedFileState } from './DocumentExplorer.types';
import { FileAction, FolderAction } from './DocumentList.types';

interface UseFileManagementOptions {
  selectedFileState: SelectedFileState | null;
  handleDeleteFiles: (files: DocumentFile[]) => void;
  handleMoveFiles: (files: DocumentFile[], targetFolderId: string | null) => void;
  invalidateContents: () => void;
  setSelectedFileState: React.Dispatch<React.SetStateAction<SelectedFileState | null>>;
}

/**
 * Owns all file CRUD interactions: deleting and moving files, cache invalidation,
 * and keeping the selected-file panel in sync after mutations.
 */
export function useFileManagement({
  selectedFileState,
  handleDeleteFiles,
  handleMoveFiles,
  invalidateContents,
  setSelectedFileState,
}: UseFileManagementOptions) {
  const { t } = useTranslation();

  const [filePendingDelete, setFilePendingDelete] = useState<DocumentFile | null>(null);
  const [filePendingMove, setFilePendingMove] = useState<DocumentFile | null>(null);

  /** Clears the selection when the currently selected file is removed. */
  const handleFileRemoved = useCallback(
    (fileId: string) => {
      setSelectedFileState((prev: SelectedFileState | null) => (prev?.file.id === fileId ? null : prev));
    },
    [setSelectedFileState]
  );

  /* const { isDeletingFiles, isMovingFiles, deleteFiles, moveFiles, invalidateContents } = useDocumentBulkActions({
    companyId,
    folderId,
    trimmedSearch,
    onFilesRemoved: (ids) => ids.forEach(handleFileRemoved),
    onFoldersRemoved: () => { },
  });
 */
  /** Patches the selected file state in-place when a file's metadata is updated, then refreshes the list. */
  const handleFileUpdated = useCallback(
    (fileId: string, name: string, category: number) => {
      setSelectedFileState((prev: SelectedFileState | null) => {
        if (!prev || prev.file.id !== fileId) return prev;
        return { ...prev, file: { ...prev.file, name, category } };
      });
      invalidateContents();
    },
    [invalidateContents, setSelectedFileState]
  );

  const handleFileDeleteConfirm = useCallback(async () => {
    if (!filePendingDelete) return;
    await handleDeleteFiles([filePendingDelete]);
    setFilePendingDelete(null);
  }, [filePendingDelete, handleDeleteFiles]);

  /** Moves the file to the selected target; skips the API call when dropped on its current folder. */
  const handleFileMoveSelect = useCallback(
    async (targetFolderId: string | null) => {
      if (!filePendingMove) return;
      await handleMoveFiles([filePendingMove], targetFolderId);
      setFilePendingMove(null);
    },
    [filePendingMove, handleMoveFiles]
  );

  /** Shows a toast for actions that are not yet fully implemented. */
  const handlePlaceholderAction = useCallback(
    (actionLabel: FolderAction, itemName?: string) => {
      toast(
        t('documents.placeholders.actionMessage', {
          action: actionLabel,
          item: itemName || t('documents.title'),
        })
      );
    },
    [t]
  );

  /** Routes file row-action labels to the corresponding handler. */
  const handleFileAction = useCallback(
    (actionLabel: FileAction, file: DocumentFile) => {

      switch (actionLabel) {
        case FileAction.Delete:
          setFilePendingDelete(file);
          return;

        case FileAction.Move:
          setFilePendingMove(file);
          return;
      }

      handlePlaceholderAction(actionLabel, file.name);
    },
    [t, handlePlaceholderAction]
  );

  return {
    filePendingDelete,
    setFilePendingDelete,
    filePendingMove,
    setFilePendingMove,
    handleFileRemoved,
    handleFileUpdated,
    handleFileAction,
    handleFileDeleteConfirm,
    handleFileMoveSelect,
    selectedFileState,
  };
}
